import { useEffect, useState } from "react";
import { Check, Minus, WhatsappLogo, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const MONTH_FULL = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];

export function waLink(phone, text) {
    const digits = String(phone || "").replace(/\D/g, "");
    if (!digits) return null;
    const intl = digits.startsWith("0") ? `62${digits.slice(1)}` : digits;
    return `https://wa.me/${intl}?text=${encodeURIComponent(text)}`;
}

export default function DuesMatrix() {
    const [year, setYear] = useState(String(new Date().getFullYear()));
    const [data, setData] = useState(null);
    const [classFilter, setClassFilter] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async (selectedYear = year) => {
        setLoading(true);
        try {
            const { data: res } = await api.get(`/finance/dues-matrix?year=${selectedYear}`);
            setData(res);
        } catch {
            toast.error("Gagal memuat rekap iuran");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const changeYear = (value) => {
        setYear(value);
        load(value);
    };

    const classes = data ? [...new Set(data.students.map((s) => s.class_name || "Tanpa Kelas"))].sort() : [];
    const query = search.trim().toLowerCase();
    const visible = data ? data.students.filter((s) => (!classFilter || (s.class_name || "Tanpa Kelas") === classFilter) && (!query || s.name.toLowerCase().includes(query))) : [];
    const grouped = classes
        .filter((c) => !classFilter || c === classFilter)
        .map((c) => ({ name: c, students: visible.filter((s) => (s.class_name || "Tanpa Kelas") === c) }));

    return (
        <div data-testid="dues-matrix">
            <div className="flex flex-wrap items-end gap-4 mb-6">
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tahun</label>
                    <input type="number" min="2025" max="2100" value={year} onChange={(e) => changeYear(e.target.value)} data-testid="dues-matrix-year" className="bg-white border border-racing-line px-4 py-3 text-sm w-32" />
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label>
                    <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)} data-testid="dues-matrix-class-filter" className="bg-white border border-racing-line px-4 py-3 text-sm">
                        <option value="">Semua Kelas</option>
                        {classes.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Cari Atlet</label>
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Ketik sebagian nama..."
                        data-testid="dues-matrix-search"
                        className="bg-white border border-racing-line px-4 py-3 text-sm w-56"
                    />
                </div>
                <p className="text-xs text-racing-muted pb-3">Klik tanda silang untuk mengirim pengingat WhatsApp ke orang tua.</p>
            </div>

            {loading && <p className="text-racing-muted">Memuat rekap...</p>}
            {!loading && data && visible.length === 0 && <p className="border border-racing-line bg-white p-6 text-sm text-racing-muted" data-testid="dues-matrix-empty">Tidak ada data atlet aktif.</p>}

            {!loading && data && grouped.map((group, gi) => (
                <div key={group.name} className="mb-8" data-testid={`dues-matrix-class-${gi}`}>
                    <p className="text-xs uppercase tracking-[0.25em] text-racing-golddeep mb-3">{group.name} — {group.students.length} atlet</p>
                    <div className="border border-racing-line overflow-x-auto bg-white">
                        <table className="text-sm">
                            <thead>
                                <tr className="border-b border-racing-line bg-racing-surface">
                                    <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-racing-muted sticky left-0 bg-racing-surface min-w-[240px]">Atlet & Kontak Ortu</th>
                                    {data.months.map((m) => (
                                        <th key={m} className="px-2 py-3 text-[10px] uppercase tracking-[0.15em] text-racing-muted text-center min-w-[46px]">{MONTH_LABELS[Number(m.slice(5, 7)) - 1]}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {group.students.map((student, si) => (
                                    <tr key={student.id} className="border-b border-racing-line last:border-0 hover:bg-black/[0.02]" data-testid={`dues-matrix-row-${gi}-${si}`}>
                                        <td className="px-4 py-2.5 sticky left-0 bg-white">
                                            <p className="font-medium text-neutral-900 text-xs md:text-sm">{student.name}</p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                                <span className="text-[10px] text-racing-muted">{student.parent_username || "—"}</span>
                                                {student.parent_phone ? (
                                                    <a
                                                        href={waLink(student.parent_phone, `Assalamu'alaikum, kami dari Mandalika Inlineskate. Ini pengingat iuran bulanan atas nama ${student.name}. Terima kasih.`)}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        data-testid={`dues-matrix-wa-${gi}-${si}`}
                                                        className="inline-flex items-center gap-1 text-[10px] text-green-700 hover:text-green-800"
                                                    >
                                                        <WhatsappLogo size={13} weight="fill" /> {student.parent_phone}
                                                    </a>
                                                ) : <span className="text-[10px] text-racing-muted">tanpa WA</span>}
                                            </div>
                                        </td>
                                        {data.months.map((m, mi) => {
                                            const paid = student.paid[m];
                                            const monthName = MONTH_FULL[Number(m.slice(5, 7)) - 1];
                                            const notRegistered = student.joined_month && m < student.joined_month;
                                            return (
                                                <td key={m} className="px-2 py-2.5 text-center" data-testid={`dues-matrix-cell-${gi}-${si}-${mi}`}>
                                                    {notRegistered ? (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-100 border border-purple-400 text-purple-600" title={`${monthName} — belum terdaftar (masuk ${student.joined_month})`} data-testid={`dues-matrix-notreg-${gi}-${si}-${mi}`}>
                                                            <Minus size={14} weight="bold" />
                                                        </span>
                                                    ) : paid ? (
                                                        <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 border border-green-600 text-green-700" title={paid.note || `Lunas Rp ${Number(paid.amount).toLocaleString("id-ID")} • ${paid.date} • ${paid.receipt_no || "-"}`}>
                                                            <Check size={14} weight="bold" />
                                                        </span>
                                                    ) : (
                                                        <a
                                                            href={student.parent_phone ? waLink(student.parent_phone, `Assalamu'alaikum, kami dari Mandalika Inlineskate mengingatkan iuran bulan ${monthName} ${data.year} atas nama ${student.name}. Terima kasih.`) : undefined}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            title={student.parent_phone ? `Belum bayar ${monthName} — klik untuk ingatkan via WhatsApp` : `Belum bayar ${monthName}`}
                                                            className="inline-flex items-center justify-center w-6 h-6 bg-red-50 border border-racing-red text-racing-red hover:bg-racing-red hover:text-white transition-colors"
                                                        >
                                                            <X size={14} weight="bold" />
                                                        </a>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
}
